import { Api } from "@/api";
import { Cart, LineItem, Order } from "@/utils/types/types";
import { WebUser } from "@prisma/client";
import { Session, User } from "@stytch/vanilla-js";
import axios from "axios";
import Cookies from "js-cookie";

export type InsertFile = {
  url: string;
  key: string;
  name: string;
  size?: number;
  uploadedAt: string;
  lineItemId?: string;
  itemNo?: number;
};
export type InsertScope = { cartId: string } | { orderId: string };

export const UserApi = {
  getUser() {
    const uid = Cookies.get("stytch_user_id");
    if (!uid) return { data: { user: undefined } };
    return Api.get<{ user: WebUser }>(`/api/user/uid/${uid}`);
  },
  updateUser(
    // Partial (2026-08-26): the checkout save-back and the add-card form
    // update billingDetails alone. Prisma ignores undefined keys, so a
    // partial payload never clears the omitted fields.
    payload: Partial<
      Pick<
        WebUser,
        | "address"
        | "firstName"
        | "lastName"
        | "billingDetails"
        | "shippingDetails"
      >
    >
  ) {
    return Api.put<{ user: WebUser }>(
      `/api/user/uid/${Cookies.get("stytch_user_id")}`,
      payload
    );
  },
  createGuestUser() {
    return Api.post("/api/user/create", {
      guid: Cookies.get("guest_user_id"),
    });
  },
  updateSession(payload: { session: Session | null; user: User | null }) {
    return Api.post("/api/user/session/update", {
      session: payload.session,
      user: payload.user,
      guid: Cookies.get("guest_user_id"),
    });
  },
  // Custom login form (2026-07-30): existing-vs-new user detection.
  // ok:false means "unknown" — the form falls back to a combined flow.
  emailStatus(email: string) {
    // legacy (2026-08-15): email appears in the seeded ASF contact export —
    // drives the "welcome back" badge on the create step.
    return Api.post<{
      ok: boolean;
      exists?: boolean;
      hasPassword?: boolean;
      legacy?: boolean;
    }>("/api/user/email-status", { email });
  },

  getOrders() {
    return Api.get<{ orders: Order[] }>(`/api/user/orders`);
  },
  getOrder(orderId: string) {
    return Api.get<{ order: Order }>(`/api/user/orders/${orderId}`);
  },
  getOrderByNo(orderNo: string) {
    return Api.get<{ order: Order }>(`/api/user/orders/by-no/${orderNo}`);
  },
  // Customer inserts / COAs (2026-10-18) — functions/api/order-inserts.ts.
  listInserts(scope: InsertScope) {
    return Api.get<{ files: InsertFile[] }>("/api/user/inserts", {
      params: scope,
    });
  },
  async uploadInsert(
    scope: InsertScope,
    lineItemId: string,
    file: File
  ): Promise<InsertFile[]> {
    const { data } = await Api.post<{
      key: string;
      presigned: { url: string; fields: Record<string, string> };
    }>("/api/user/inserts/presign", {
      ...scope,
      lineItemId,
      fileName: file.name,
      fileType: "application/pdf",
      size: file.size,
    });
    const form = new FormData();
    Object.entries(data.presigned.fields).forEach(([k, v]) =>
      form.append(k, v)
    );
    form.append("file", file);
    await axios.post(data.presigned.url, form);
    const confirmed = await Api.post<{ files: InsertFile[] }>(
      "/api/user/inserts/confirm",
      { ...scope, lineItemId, key: data.key, name: file.name }
    );
    return confirmed.data.files || [];
  },
  removeInsert(cartId: string, lineItemId: string) {
    return Api.post<{ files: InsertFile[] }>("/api/user/inserts/remove", {
      cartId,
      lineItemId,
    });
  },
  // Admin methods
  getUserByUid(uid: string) {
    return Api.get<{ user: WebUser }>(`/api/user/uid/${uid}`);
  },
  getOrdersByUid(uid: string) {
    return Api.get<{ orders: Order[] }>(`/api/user/orders?uid=${uid}`);
  },
  deleteUserByUid(uid: string) {
    return Api.delete<{
      ok: boolean;
      message: string;
      deletedUser: { uid: string; email: string; guid: string };
    }>(`/api/user/uid/${uid}`);
  },

  getCart() {
    return Api.post<{ ok: boolean; userCart: Cart }>(`/api/user/cart`, {
      guid: Cookies.get("guest_user_id"),
      uid: Cookies.get("stytch_user_id"),
    });
  },

  addLineItem(payload: Partial<LineItem>) {
    return Api.post<{ ok: boolean; lineItem: LineItem }>(`api/user/cart/add`, {
      guid: Cookies.get("guest_user_id"),
      uid: Cookies.get("stytch_user_id"),
      ...payload,
    });
  },
  updateLineItem(payload: Partial<LineItem>) {
    return Api.post<{ ok: boolean }>(`api/user/lineItem/update`, {
      guid: Cookies.get("guest_user_id"),
      uid: Cookies.get("stytch_user_id"),
      ...payload,
    });
  },
  removeLineItem(payload: { id: string }) {
    return Api.post<{ ok: boolean }>(`/api/user/lineItem/remove`, {
      guid: Cookies.get("guest_user_id"),
      uid: Cookies.get("stytch_user_id"),
      ...payload,
    });
  },
  updatePassword(payload: { currentPassword: string; newPassword: string }) {
    return Api.post<{
      ok: boolean;
      message: string;
      session?: any;
      session_token?: string;
    }>("/api/user/change-password", payload);
  },
};
